import { useEffect, useRef, useState } from 'react';

import { useQueryClient } from 'react-query';
import { SSE } from 'sse.js';

import { QueryEntity } from '../api/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

export interface JobProgressEvent {
  type:
    | 'zip-extract-progress'
    | 'zip-extract-complete'
    | 'relativity-import-progress'
    | 'relativity-import-complete'
    | 'relativity-import-error'
    | 'NoActiveJobs'
    | 'InitialState';
  jobId?: string;
  jobType?: 'unzip' | 'relativity';
  processedFiles?: number;
  totalFiles?: number;
  fileName?: string;
  error?: string;
  activeJobs?: any[];
}

export interface ActiveJob {
  id: string;
  type: 'unzip' | 'relativity';
  fileName: string;
  totalFiles: number;
  processedFiles: number;
  status: 'processing' | 'completed' | 'error';
}

export const useJobProgress = (caseId: string) => {
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const sseRef = useRef<SSE | null>(null);
  const { fetchConfigGET } = useGetFetchConfig();
  const queryClient = useQueryClient();

  const closeSSE = () => {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    setReconnectTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    if (!caseId) return;

    const setupSSE = () => {
      try {
        const sseUrl = `${APIBaseChronos}/api/storage/job-events/${caseId}`;

        const eventSource = new SSE(sseUrl, {
          headers: Object.fromEntries(fetchConfigGET.headers.entries()),
        });

        eventSource.onmessage = (event: any) => {
          try {
            if (!event.data || !event.data.trim()) {
              return;
            }

            const data: JobProgressEvent = JSON.parse(event.data);

            setActiveJobs((prevJobs) => {
              // Handle no active jobs message
              if (data.type === 'NoActiveJobs') {
                return [];
              }

              // Handle initial state message - just placeholder jobs for now
              if (data.type === 'InitialState') {
                return prevJobs; // Keep existing jobs, actual progress will come in subsequent messages
              }

              // Early return if required fields are missing
              if (!data.jobId || !data.jobType) {
                return prevJobs;
              }

              const existingJobIndex = prevJobs.findIndex((job) => job.id === data.jobId);

              if (data.type === 'zip-extract-progress' || data.type === 'relativity-import-progress') {
                const updatedJob: ActiveJob = {
                  id: data.jobId,
                  type: data.jobType,
                  fileName: data.fileName || 'Unknown',
                  totalFiles: data.totalFiles || 0,
                  processedFiles: data.processedFiles || 0,
                  status: 'processing',
                };

                if (existingJobIndex >= 0) {
                  // Update existing job
                  const newJobs = [...prevJobs];
                  newJobs[existingJobIndex] = updatedJob;
                  return newJobs;
                } else {
                  // Add new job
                  return [...prevJobs, updatedJob];
                }
              } else if (data.type === 'zip-extract-complete' || data.type === 'relativity-import-complete') {
                // Remove completed job and close SSE connection
                closeSSE();
                queryClient.invalidateQueries({ queryKey: [QueryEntity.Case, caseId] });
                return prevJobs.filter((job) => job.id !== data.jobId);
              } else if (data.type === 'relativity-import-error') {
                // Update job status to error
                if (existingJobIndex >= 0) {
                  const newJobs = [...prevJobs];
                  newJobs[existingJobIndex] = {
                    ...newJobs[existingJobIndex],
                    status: 'error',
                  };
                  return newJobs;
                }
              }

              return prevJobs;
            });
          } catch (error) {
            console.error('Error parsing SSE message:', error);
            console.error('Raw event data:', event.data);
            closeSSE();
          }
        };

        eventSource.onerror = (error: any) => {
          console.error('SSE error:', error);
          closeSSE();
        };

        sseRef.current = eventSource;
      } catch (error) {
        console.error('Error setting up SSE:', error);
        closeSSE();
      }
    };

    setupSSE();

    return () => {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [caseId, reconnectTrigger]);

  return { activeJobs };
};
